/**
 * generateNewsArticleJsonLd — emits 3-block JSON-LD stack per news detail
 * page, per design source §3.2 + GTM/GNPC-APPLICATION-KIT §4.
 *
 * Block 1: NewsArticle (always)
 * Block 2: BreadcrumbList (always)
 * Block 3: Conditional — Day-1 omitted (citation linker deferred):
 *   - Mode A → ClaimReview (when citations populated, Phase 2)
 *   - Mode B → Legislation (when reference graph populated, Phase 2)
 *
 * Per founder re-prioritization 2026-05-05: Day-1 = NewsArticle +
 * BreadcrumbList only. The 3rd block lights up automatically once
 * citation/legislation data is present in the news_item row.
 */

import type { NewsDetailResponse } from './news-api';

const ORIGIN = 'https://asklaw.vn';
const PUBLISHER_LOGO = `${ORIGIN}/logo-1000.png`;

export interface NewsArticleSchema {
  '@context': 'https://schema.org';
  '@type': 'NewsArticle';
  headline: string;
  description?: string;
  datePublished: string;
  dateModified: string;
  author: { '@type': 'Organization'; name: string };
  publisher: {
    '@type': 'Organization';
    name: string;
    logo: { '@type': 'ImageObject'; url: string };
  };
  mainEntityOfPage: { '@type': 'WebPage'; '@id': string };
  inLanguage: 'vi' | 'en';
  isAccessibleForFree: true;
  isBasedOn?: string;
}

export interface BreadcrumbListSchema {
  '@context': 'https://schema.org';
  '@type': 'BreadcrumbList';
  itemListElement: Array<{
    '@type': 'ListItem';
    position: number;
    name: string;
    item: string;
  }>;
}

export type SchemaBlock = NewsArticleSchema | BreadcrumbListSchema | Record<string, unknown>;

export function generateNewsArticleJsonLd(
  news: NewsDetailResponse,
  lang: 'vi' | 'en',
): SchemaBlock[] {
  const url = `${ORIGIN}/news/${news.slug}`;
  const publishedIso = new Date(news.published_at * 1000).toISOString();
  const modifiedIso = new Date(news.ingested_at * 1000).toISOString();

  const description = news.ai_summary
    ? news.ai_summary.slice(0, 250)
    : news.raw_excerpt
      ? news.raw_excerpt.slice(0, 250)
      : news.headline.slice(0, 250);

  const article: NewsArticleSchema = {
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    headline: news.headline,
    description,
    datePublished: publishedIso,
    dateModified: modifiedIso,
    author: { '@type': 'Organization', name: 'Asklaw Editorial Team' },
    publisher: {
      '@type': 'Organization',
      name: 'Asklaw',
      logo: { '@type': 'ImageObject', url: PUBLISHER_LOGO },
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
    inLanguage: lang,
    isAccessibleForFree: true,
    isBasedOn: news.source_url,
  };

  const breadcrumbsName = lang === 'en' ? 'Legal news' : 'Tin pháp luật';
  const homeName = lang === 'en' ? 'Home' : 'Trang chủ';

  const breadcrumbs: BreadcrumbListSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: homeName,
        item: ORIGIN,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: breadcrumbsName,
        item: `${ORIGIN}/news`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: news.headline,
        item: url,
      },
    ],
  };

  const blocks: SchemaBlock[] = [article, breadcrumbs];

  // Conditional 3rd block — Day-1 omitted per founder re-prioritization.
  // When news.primary_vbpl_doc_id populates (Mode B reference graph S5-late),
  // append a Legislation block referencing the primary VBPL doc. When citations
  // populate (Mode A), append a ClaimReview block.
  // Safe-guard: only emit when the data exists, never fake.
  if (news.detail_mode === 'new_law' && news.primary_vbpl_doc_id) {
    blocks.push({
      '@context': 'https://schema.org',
      '@type': 'Legislation',
      name: news.primary_vbpl_provision ?? news.headline,
      legislationIdentifier: news.primary_vbpl_doc_id,
      legislationJurisdiction: 'VN',
      inLanguage: lang,
      isPartOf: { '@type': 'CreativeWork', name: 'Vietnam VBPL Corpus' },
    });
  }

  return blocks;
}

/**
 * Serialise schema blocks to <script type="application/ld+json"> tag bodies.
 * Returned strings should be injected in <head> via the route SSR or via a
 * useEffect createElement('script') flow.
 */
export function renderJsonLdScripts(blocks: SchemaBlock[]): string[] {
  return blocks.map((b) => JSON.stringify(b));
}
